import { Icon } from '@iconify/react';
import { Button, Card, CardBody } from '@heroui/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useSegment from '@/hooks/useSegment';
import {
  answerToChartLabel,
  labelForWeek,
} from '@/patient-app-common/helpers/progress_helper';

import { EntryList } from '../EntryList';

import { MultiLineChart } from './MultiLineChart';

const EVENT = 'Progress chart date changed';

export function WeeklyMultiLineData({ question }) {
  const { t } = useTranslation('progress');
  const { sendTrackEvent } = useSegment();

  const [startOfWeek, setStartOfWeek] = useState(dayjs().startOf('isoWeek'));

  const trackNavigation = (direction, date) => {
    const eventData = {
      id: question.id,
      direction,
      procedureId: question.procedureId,
      procedureName: question.surveyName,
      questionType: question.type,
      startDate: dayjs(date).format('YYYY-MM-DD').toString(),
      timeFrame: 'week',
      title: question.title,
    };

    sendTrackEvent(EVENT, eventData);
  };

  const getPrevWeek = () => {
    const prevWeek = dayjs(startOfWeek).subtract(1, 'weeks');
    setStartOfWeek(prevWeek);
    trackNavigation('back', prevWeek);
  };

  const disableBack = useMemo(() => {
    const { patientEntries } = question;
    const firstEntryDate = _.first(patientEntries).date;
    return dayjs(firstEntryDate).isSameOrAfter(startOfWeek, 'isoWeek');
  }, [question, startOfWeek]);

  const getNextWeek = () => {
    const nextWeek = dayjs(startOfWeek).add(1, 'weeks');
    setStartOfWeek(nextWeek);
    trackNavigation('next', nextWeek);
  };

  const disableNext = useMemo(
    () => dayjs().isSameOrBefore(startOfWeek, 'isoWeek'),
    [startOfWeek]
  );

  const entriesForWeek = useMemo(() => {
    const { patientEntries } = question;
    const data = [];

    for (let i = 0; i <= 6; i += 1) {
      const dayOfWeek = dayjs(startOfWeek).add(i, 'days');

      // exclude future entries
      if (dayjs().isBefore(dayOfWeek)) break;

      // note this resets entry date to 12am so that the scatter plot lines up with the x-axis
      let datum = { date: dayOfWeek, skipped: false };

      // if there are multiple entries on the same day, get the last entry
      const entry = _.findLast(patientEntries, ({ date }) =>
        dayjs(date).isSame(dayOfWeek, 'day')
      );

      if (entry) {
        datum = { ...datum, ...entry };
      } else {
        datum.skipped = true;
        question.answers.forEach((_answer, i) => {
          datum[`answer_${i}`] = 0;
        });
      }
      data.push(datum);
    }

    return data;
  }, [startOfWeek, question]);

  const average = useMemo(() => {
    const entries = entriesForWeek.filter((entry) => !entry.skipped);

    if (!entries.length) return null;

    const values = question.answers.map((_answer, i) => {
      const value = _.mean(entries.map((entry) => entry[`answer_${i}`]));
      return _.round(value);
    });

    const display = `${answerToChartLabel(question, values)} ${question.unit}`;
    return display;
  }, [entriesForWeek, question.unit]);

  return (
    <>
      <h3 className="text-sm">{labelForWeek(startOfWeek)}</h3>
      <div className="flex flex-row">
        <Button
          color="primary"
          isDisabled={disableBack}
          variant="light"
          onPress={getPrevWeek}
        >
          <Icon icon="mdi:chevron-left" />
          {t('common.action.back')}
        </Button>

        <Button
          color="primary"
          isDisabled={disableNext}
          variant="light"
          onPress={getNextWeek}
        >
          {t('common.action.next')}
          <Icon icon="mdi:chevron-right" />
        </Button>
      </div>
      <MultiLineChart
        question={question}
        filteredEntries={entriesForWeek}
        mode="week"
      />
      {question.metricTracker && average && (
        <Card className="bg-primary-100" shadow="none">
          <CardBody className="font-light text-center">
            {t('weeklymultilinedata.average_text', { average })}
          </CardBody>
        </Card>
      )}
      {!question.metricTracker && (
        <EntryList
          question={question}
          filteredEntries={entriesForWeek}
          mode="day"
        />
      )}
    </>
  );
}
